import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import toast from 'react-hot-toast';
import { deleteAppointmentGalleryFile } from '../../store/crm/appointment/attachment';
import {
    deleteCrmCustomerGalleryFile,
    getCrmCustomerGallery,
} from '../../store/crm/crm/crm-details';
import { MySwal } from '../../services/utills';
import { mySwalButtonCancel, mySwalButtonYes } from '../card-appointment/CardAppointment';
import Waveloading from '../../pages/loading/Waveloading';
import CustomModal from './customModal';

const PURPLE = '#A72C68';
const PURPLE_LIGHT = '#FAEFF5';
const PURPLE_BORDER = '#F0CDDB';

const CrmGalleryPanel = ({ user_id, onClose, onDone }) => {
    const dispatch = useDispatch();
    const [activeView, setActiveView] = useState('card');
    const [selectedAlbum, setSelectedAlbum] = useState('all');
    const [checkedFiles, setCheckedFiles] = useState([]);
    const [modalImage, setModalImage] = useState(null);
    const [loading, setLoading] = useState(false);

    const crmDetailsStore = useSelector((state) => state?.crmDetailsStore);
    const { customerGalleries = [] } = crmDetailsStore;

    useEffect(() => {
        if (!user_id) return;
        (async () => {
            setLoading(true);
            await dispatch(getCrmCustomerGallery(user_id));
            setLoading(false);
        })();
    }, [user_id]);

    const albums = useMemo(() => {
        const list = [];
        customerGalleries?.data?.forEach((a) => list.push({ id: `c-${a.id}`, name: a.tag?.tag_name, source: 'customer', raw: a }));
        customerGalleries?.appointments?.forEach((a) => list.push({ id: `a-${a.id}`, name: a.tag?.tag_name, source: 'appointment', raw: a }));
        return list;
    }, [customerGalleries]);

    const flatFiles = useMemo(() => {
        const files = [];
        const collect = (arr, source) => {
            arr?.forEach((album) => {
                album?.files?.forEach((f) => {
                    files.push({
                        ...f,
                        albumId: `${source === 'customer' ? 'c' : 'a'}-${album.id}`,
                        albumName: album?.tag?.tag_name || '',
                        source,
                    });
                });
            });
        };
        collect(customerGalleries?.data, 'customer');
        collect(customerGalleries?.appointments, 'appointment');
        if (selectedAlbum === 'all') return files;
        return files.filter((f) => f.albumId === selectedAlbum);
    }, [customerGalleries, selectedAlbum]);

    const toggleChecked = (id) => {
        setCheckedFiles((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleDeleteFile = async (file) => {
        const confirm = await MySwal.fire({
            title: 'Are you sure ?',
            showConfirmButton: false,
            customClass: { icon: 'custom-icon-size', popup: 'custom-popup-shadow', closeButton: 'custom-close-button' },
            showCloseButton: true,
            html: (
                <Box>
                    <Stack sx={{ width: '100%', justifyContent: 'center' }} direction={{ xs: 'column', lg: 'row', sm: 'row' }} spacing={2}>
                        <Button sx={{ ...mySwalButtonYes }} onClick={() => MySwal.clickConfirm()}>Yes, Delete</Button>
                        <Button sx={{ ...mySwalButtonCancel }} onClick={() => MySwal.close()}>NO</Button>
                    </Stack>
                </Box>
            ),
        });
        if (confirm?.isConfirmed) {
            setLoading(true);
            if (file.source === 'appointment') {
                await dispatch(deleteAppointmentGalleryFile({ id: file.id, booking_id: user_id }));
            } else {
                await dispatch(deleteCrmCustomerGalleryFile({ id: file.id, customer_id: user_id }));
            }
            await dispatch(getCrmCustomerGallery(user_id));
            toast.success('Gallery file deleted successfully');
            setCheckedFiles((prev) => prev.filter((x) => x !== file.id));
            setLoading(false);
        }
    };

    const renderCard = (file) => {
        const isChecked = checkedFiles.includes(file.id);
        return (
            <Box
                key={`${file.albumId}-${file.id}`}
                sx={{
                    border: isChecked ? `2px solid ${PURPLE}` : '1px solid #ECEDF2',
                    borderRadius: '12px',
                    p: '10px',
                    background: '#fff',
                    position: 'relative',
                    transition: 'all 0.15s',
                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
                }}
            >
                <Box sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', aspectRatio: '1 / 1', background: '#F7F8FB' }}>
                    <Box
                        component="img"
                        src={file?.file}
                        alt=""
                        onClick={() => setModalImage(file?.file)}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer', display: 'block' }}
                    />
                    <Checkbox
                        checked={isChecked}
                        onChange={() => toggleChecked(file.id)}
                        icon={<Box sx={{ width: 22, height: 22, borderRadius: '6px', background: '#fff', border: '1.5px solid #d1d5db' }} />}
                        checkedIcon={
                            <Box sx={{ width: 22, height: 22, borderRadius: '6px', background: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
                            </Box>
                        }
                        sx={{ position: 'absolute', top: 6, right: 6, p: 0 }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '10px' }}>
                    <Typography sx={{ fontSize: '13px', color: '#3b4252', fontWeight: 600 }}>
                        {file?.created_at ? moment(file.created_at).format('YYYY-MM-DD') : '—'}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteFile(file)}
                        sx={{
                            p: 0.5,
                            background: '#FFEBEB',
                            color: '#EB5757',
                            borderRadius: '6px',
                            '&:hover': { background: '#FFD3D3' },
                        }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>
        );
    };

    const renderRow = (file) => {
        const isChecked = checkedFiles.includes(file.id);
        return (
            <Box
                key={`${file.albumId}-${file.id}`}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: '10px 12px',
                    border: '1px solid #ECEDF2', borderRadius: '10px', mb: '8px', background: '#fff',
                    '&:hover': { background: '#FAFAFC' },
                }}
            >
                <Avatar src={file?.file} variant="rounded" sx={{ width: 44, height: 44, cursor: 'pointer' }} onClick={() => setModalImage(file?.file)} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', color: '#1a1a2e', fontWeight: 600, textTransform: 'capitalize' }}>
                        {file.albumName || 'Untitled'}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#8a8d97' }}>
                        {file?.created_at ? moment(file.created_at).format('YYYY-MM-DD') : '—'}
                    </Typography>
                </Box>
                <Checkbox
                    checked={isChecked}
                    onChange={() => toggleChecked(file.id)}
                    icon={<Box sx={{ width: 20, height: 20, borderRadius: '5px', background: '#fff', border: '1.5px solid #d1d5db' }} />}
                    checkedIcon={
                        <Box sx={{ width: 20, height: 20, borderRadius: '5px', background: PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
                        </Box>
                    }
                    sx={{ p: 0 }}
                />
                <IconButton size="small" onClick={() => handleDeleteFile(file)} sx={{ p: 0.5, background: '#FFEBEB', color: '#EB5757', borderRadius: '6px', '&:hover': { background: '#FFD3D3' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 2, borderBottom: '1px solid #F1F2F6', flexShrink: 0 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                    background: `linear-gradient(135deg, ${PURPLE} 0%, #921F58 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(167,44,104,0.25)',
                }}>
                    <PhotoLibraryIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#1a1a2e', lineHeight: 1.2, fontFamily: 'helvetica-lt-std-bold,sans-serif' }}>
                        Photos
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#8a8d97', mt: '2px' }}>
                        Manage and organize patient photos.
                    </Typography>
                </Box>
                <Tooltip title="Close" placement="bottom">
                    <IconButton size="small" onClick={onClose} sx={{ color: '#9ca3af', borderRadius: '8px', '&:hover': { color: '#ef4444', background: '#fef2f2' } }}>
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Body */}
            <Box sx={{
                flex: 1, overflowY: 'auto', p: 2,
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: PURPLE_LIGHT, borderRadius: '8px' },
                '&::-webkit-scrollbar-thumb': { background: PURPLE, borderRadius: '8px', border: `2px solid ${PURPLE_LIGHT}` },
                '&::-webkit-scrollbar-thumb:hover': { background: '#921F58' },
                scrollbarColor: `${PURPLE} ${PURPLE_LIGHT}`,
                scrollbarWidth: 'thin',
            }}>
                {/* Gallery title + view toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a2e', fontFamily: 'helvetica-lt-std-bold,sans-serif' }}>
                        Gallery
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '4px', background: PURPLE_LIGHT, borderRadius: '8px', p: '3px' }}>
                        <IconButton size="small" onClick={() => setActiveView('card')} sx={{
                            borderRadius: '6px',
                            background: activeView === 'card' ? PURPLE : 'transparent',
                            color: activeView === 'card' ? '#fff' : PURPLE,
                            '&:hover': { background: activeView === 'card' ? '#921F58' : 'rgba(167,44,104,0.12)' },
                        }}>
                            <DashboardIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setActiveView('list')} sx={{
                            borderRadius: '6px',
                            background: activeView === 'list' ? PURPLE : 'transparent',
                            color: activeView === 'list' ? '#fff' : PURPLE,
                            '&:hover': { background: activeView === 'list' ? '#921F58' : 'rgba(167,44,104,0.12)' },
                        }}>
                            <ViewStreamIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Album selector */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                        value={selectedAlbum}
                        onChange={(e) => setSelectedAlbum(e.target.value)}
                        sx={{
                            borderRadius: '10px',
                            fontSize: '13px',
                            background: '#fff',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PURPLE_BORDER },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PURPLE },
                        }}
                    >
                        <MenuItem value="all">All Albums</MenuItem>
                        {albums.map((a) => (
                            <MenuItem key={a.id} value={a.id}>{a.name || 'Untitled'}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Gallery body */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <Waveloading />
                    </Box>
                ) : flatFiles.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af', fontSize: '13px' }}>
                        No photos found.
                    </Box>
                ) : activeView === 'card' ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
                        {flatFiles.map(renderCard)}
                    </Box>
                ) : (
                    <Box>{flatFiles.map(renderRow)}</Box>
                )}

            </Box>

            <CustomModal customWidth="1000px" mdWidthImage="80%" open={modalImage !== null} setOpen={() => setModalImage(null)}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box component="img" src={modalImage} alt="" sx={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px', objectFit: 'contain' }} />
                </Box>
            </CustomModal>
        </Box>
    );
};

export default CrmGalleryPanel;
